import Experience from './Experience.js'
import { profile, chapters, works } from './zhuyuData.js'

/*
 * 页面编排（五幕）：
 *   0 房间序幕 → 滚动退场（关灯 + 镜头拉远 + 淡出）
 *   1 开场白（左文右照片卡，鼠标视差）
 *   2 关于（文案分行渐显 + 数据卡数字滚动 + 标签梯次入场）
 *   3 作品集（胶片条横向滑动 + 置顶大卡）
 *   4 尾声（跑马灯 + 大字）  X 彩蛋
 */
export default class ZhuyuPage
{
    constructor()
    {
        this.experience = new Experience()
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        this.lightBase = null
        this.statsPlayed = false

        this.renderIntro()
        this.renderWorks()
        this.setScrollCue()
        this.setReveal()
        this.setTypewriter()
        this.setPhotoParallax()
        this.setExitChoreography()
    }

    esc(s)
    {
        return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    }

    /* ---------- 第 1/2 幕 ---------- */
    renderIntro()
    {
        document.getElementById('introBg').src = '/assets/zhuyu/sunset.jpg'
        document.getElementById('kickerAvatar').src = '/assets/zhuyu/avatar.jpeg'
        document.getElementById('navAvatar').src = '/assets/zhuyu/avatar.jpeg'
        document.getElementById('photoCap').textContent = profile.photoCaption
        document.getElementById('douyinBtn').href = profile.douyinUrl
        document.getElementById('navDouyin').href = profile.douyinUrl
        document.getElementById('douyinFoot').href = profile.douyinUrl

        const lines = document.getElementById('introLines')
        profile.introLines.forEach((t) =>
        {
            const p = document.createElement('p')
            p.textContent = t
            lines.appendChild(p)
        })

        const stats = document.getElementById('stats')
        profile.stats.forEach((st) =>
        {
            const d = document.createElement('div')
            d.className = 'stat'
            d.innerHTML = '<b data-num="' + (st.num === null ? '' : st.num) + '" data-display="' + this.esc(st.display) + '">' +
                (st.num === null ? this.esc(st.display) : '0') + '</b><span class="mono">' + this.esc(st.label) + '</span>'
            stats.appendChild(d)
        })

        const tags = document.getElementById('tags')
        profile.tags.forEach((t, i) =>
        {
            const s = document.createElement('span')
            s.className = 'tag'
            s.textContent = t
            s.style.transitionDelay = (i * 55) + 'ms'
            tags.appendChild(s)
        })
    }

    animateStats()
    {
        document.querySelectorAll('.stat b').forEach((b) =>
        {
            const numAttr = b.dataset.num
            if(numAttr === '') return
            const target = parseInt(numAttr, 10)
            const display = b.dataset.display
            const dur = this.reduceMotion ? 1 : 1300
            let t0 = null
            const frame = (ts) =>
            {
                if(!t0) t0 = ts
                const p = Math.min((ts - t0) / dur, 1)
                const eased = 1 - Math.pow(1 - p, 3)
                b.textContent = p === 1 ? display : Math.round(target * eased).toLocaleString()
                if(p < 1) requestAnimationFrame(frame)
            }
            requestAnimationFrame(frame)
        })
    }

    setTypewriter()
    {
        const target = document.getElementById('typeTarget')
        const text = profile.tagline
        if(this.reduceMotion)
        {
            target.textContent = text
            return
        }
        let started = false
        const io = new IntersectionObserver((entries) =>
        {
            if(!entries[0].isIntersecting || started) return
            started = true
            io.disconnect()
            let i = 0
            const tick = () =>
            {
                if(i <= text.length)
                {
                    target.textContent = text.slice(0, i)
                    i++
                    setTimeout(tick, 60 + Math.random() * 55)
                }
            }
            tick()
        }, { threshold: 0.4 })
        io.observe(document.getElementById('intro'))
    }

    /* 照片卡鼠标视差 */
    setPhotoParallax()
    {
        if(this.reduceMotion) return
        const card = document.getElementById('photoCard')
        const hero = document.getElementById('intro')
        if(!card || !hero || !window.matchMedia('(pointer: fine)').matches) return
        hero.addEventListener('mousemove', (e) =>
        {
            const r = hero.getBoundingClientRect()
            const x = (e.clientX - r.left) / r.width - 0.5
            const y = (e.clientY - r.top) / r.height - 0.5
            card.style.transform = 'rotate(2.2deg) rotateY(' + (x * 7) + 'deg) rotateX(' + (-y * 6) + 'deg) translateY(' + (y * -6) + 'px)'
        })
        hero.addEventListener('mouseleave', () =>
        {
            card.style.transform = ''
        })
    }

    /* ---------- 第 3 幕：胶片条 ---------- */
    renderWorks()
    {
        const box = document.getElementById('chaptersBox')
        document.getElementById('worksCount').textContent = works.length
        chapters.forEach((ch) =>
        {
            const sec = document.createElement('div')
            sec.className = 'chapter reveal'

            /* 幽灵大编号 + 标题 + 副题 */
            sec.innerHTML =
                '<div class="chap-head">' +
                '<span class="chap-ghost mono" aria-hidden="true">' + ch.no + '</span>' +
                '<div class="chap-title"><span class="chap-no mono">CHAPTER ' + ch.no + '</span>' +
                '<h3>' + this.esc(ch.title) + '</h3>' +
                '<span class="chap-sub mono">' + this.esc(ch.sub) + '</span></div></div>'

            if(ch.id === 'next')
            {
                sec.innerHTML +=
                    '<div class="next-card"><span class="big">🎬</span>' +
                    '<span>未完待续 —— 新的城市、新的镜头、新的故事。</span>' +
                    '<span class="mono">ANYTHING IS POSSIBLE</span></div>'
                box.appendChild(sec)
                return
            }

            const list = works.filter((w) => w.chapter === ch.id)
            const strip = document.createElement('div')
            strip.className = 'works-strip'
            list.forEach((w, i) =>
            {
                const a = document.createElement('a')
                /* 统一规则：每章第一张 = 加宽主打卡（232px），其余 176px */
                a.className = 'work-card' + (i === 0 ? ' featured' : '')
                a.href = 'https://www.douyin.com/video/' + w.id
                a.target = '_blank'
                a.rel = 'noopener'
                a.innerHTML =
                    '<span class="film-edge" aria-hidden="true"></span>' +
                    '<div class="work-cover"><img loading="lazy" src="/assets/zhuyu/covers/' + w.id + '.jpeg" alt="' + this.esc(w.title) + ' 封面">' +
                    '<span class="cover-shade" aria-hidden="true"></span>' +
                    (w.pinned ? '<span class="work-pin mono">置顶</span>' : '') +
                    '<span class="work-jump mono">↗</span>' +
                    '<span class="work-likes mono">♥ ' + w.likes.toLocaleString() + '</span></div>' +
                    '<div class="work-info"><div class="work-title">' + this.esc(w.title) + '</div>' +
                    '<span class="work-tag mono">' + this.esc(w.tag || '') + '</span></div>'
                strip.appendChild(a)
            })
            /* 短章节补一张"拍摄中"占位卡，避免胶片条显得空 */
            if(list.length < 3)
            {
                const ph = document.createElement('div')
                ph.className = 'work-card placeholder'
                ph.innerHTML = '<span class="ph-icon">🎞️</span><span class="ph-text mono">下一条 · 拍摄中</span>'
                strip.appendChild(ph)
            }

            sec.appendChild(strip)
            if(list.length > 4)
            {
                const hint = document.createElement('div')
                hint.className = 'strip-hint mono'
                hint.textContent = '⟵ 拖动浏览 · ' + list.length + ' 条 ⟶'
                sec.appendChild(hint)
            }
            box.appendChild(sec)
            this.makeDraggable(strip)
        })
    }

    /* 桌面端按住拖动横滑（区分拖动与点击） */
    makeDraggable(strip)
    {
        let down = null
        strip.addEventListener('pointerdown', (e) =>
        {
            if(e.pointerType !== 'mouse') return
            down = { x: e.clientX, left: strip.scrollLeft, moved: false }
        })
        window.addEventListener('pointermove', (e) =>
        {
            if(!down) return
            const dx = e.clientX - down.x
            if(Math.abs(dx) > 5)
            {
                down.moved = true
                strip.classList.add('dragging')
            }
            strip.scrollLeft = down.left - dx
        })
        window.addEventListener('pointerup', () =>
        {
            if(!down) return
            strip.classList.remove('dragging')
            down = null
        })
        /* 拖动后拦截 click，避免误跳转 */
        strip.addEventListener('click', (e) =>
        {
            if(strip.classList.contains('dragging')) e.preventDefault()
        }, true)
        strip.addEventListener('dragstart', (e) => e.preventDefault())
    }

    /* ---------- 第 0 幕退场 ---------- */
    setExitChoreography()
    {
        const canvas = document.getElementById('experienceEl')
        const heroUi = document.getElementById('heroUi')
        const nav = document.getElementById('topnav')

        const seg = (p, a, b) => Math.min(Math.max((p - a) / (b - a), 0), 1)

        const apply = () =>
        {
            const p = Math.min(Math.max(window.scrollY / (window.innerHeight * 0.92), 0), 1)

            this.experience.exitProgress = this.reduceMotion ? 0 : p

            const baked = this.experience.world && this.experience.world.baked
            if(baked)
            {
                const u = baked.model.material.uniforms
                if(!this.lightBase)
                {
                    this.lightBase = {
                        desk: u.uLightDeskStrength.value,
                        tv: u.uLightTvStrength.value,
                        pc: u.uLightPcStrength.value
                    }
                }
                u.uLightDeskStrength.value = this.lightBase.desk * (1 - seg(p, 0.05, 0.35))
                u.uLightTvStrength.value = this.lightBase.tv * (1 - seg(p, 0.25, 0.6))
                u.uLightPcStrength.value = this.lightBase.pc * (1 - seg(p, 0.45, 0.8))
            }

            const fade = 1 - seg(p, 0.55, 0.96)
            canvas.style.opacity = fade
            canvas.style.visibility = fade <= 0.01 ? 'hidden' : 'visible'

            heroUi.style.opacity = 1 - seg(p, 0, 0.4)
            heroUi.style.pointerEvents = p > 0.3 ? 'none' : ''

            nav.classList.toggle('on', p > 0.85)
        }

        window.addEventListener('scroll', apply, { passive: true })
        apply()
    }

    /* ---------- 杂项 ---------- */
    setScrollCue()
    {
        document.getElementById('scrollCue').addEventListener('click', () =>
        {
            document.getElementById('page').scrollIntoView({ behavior: 'smooth' })
        })
        document.querySelector('.nav-id').addEventListener('click', (e) =>
        {
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: 'smooth' })
        })
    }

    setReveal()
    {
        const io = new IntersectionObserver((entries) =>
        {
            entries.forEach((en) =>
            {
                if(en.isIntersecting)
                {
                    en.target.classList.add('in')
                    if(en.target.id === 'stats' && !this.statsPlayed)
                    {
                        this.statsPlayed = true
                        this.animateStats()
                    }
                    io.unobserve(en.target)
                }
            })
        }, { threshold: 0.12 })
        document.querySelectorAll('.reveal, .section, .intro-lines, .tags, #stats').forEach((n) => io.observe(n))
    }
}
